import {
  ComparatorContext,
  ComponentDescriptor,
  getRendererFromDescriptor,
} from '@kizi/comparator-core';
import { Div, DivProps, Label, LabelProps, P } from '@kizi/core-components';
import {
  IconV2,
  IconV2Props,
  TooltipProps,
  withTooltip,
} from '@kizi/shared-components';
import React, { ComponentType, useContext, useMemo } from 'react';
import { useTooltipTracking } from '../../utils/hooks';

export interface BaseWithFormProps<P extends object = any> {
  _label?: string | React.ReactNode;
  _labelProps?: LabelProps;
  _footnote?: string | React.ReactNode;
  _description?: string | React.ReactNode;
  _tooltip?: TooltipProps<IconV2Props>['_tooltip'] | ComponentDescriptor;
  _error?: string | string[];
  _containerProps?: DivProps;
  _innerContainerProps?: DivProps;
  _errorProps?: BaseWithFormProps<P> & P;
  _successProps?: BaseWithFormProps<P> & P;
  _options?: { label: string; value: any }[];
  customBlur?: (e) => React.FocusEvent<HTMLInputElement>;
  customRadioChange?: (e) => React.FocusEvent<HTMLInputElement>;
  customCheckboxChange?: (e) => React.FocusEvent<HTMLInputElement>;
  propMap?: any;
  customSideLabel?: any;
  shouldTriggerAPICall?: boolean;
}

interface InputTypeProps {
  value?: any;
  [key: string]: any;
}

export type WithFormProps<Props extends object> = Props &
  BaseWithFormProps<Props>;

export interface Options {
  _containerProps?: DivProps;
}

const TooltipIcon = withTooltip(IconV2);

const getIconColor = (comparatorName: string) => {
  const themes = {
    os: { iconColor: '#BEC0C3' },
    avz: { iconColor: '#cbcfd4' },
    cb: { iconColor: 'main.1' },
    default: { iconColor: 'main.1' },
  };

  if (
    comparatorName.startsWith('os-') ||
    comparatorName === 'moneysure-event-based'
  ) {
    return themes.os;
  }

  if (comparatorName === 'avz' || comparatorName.startsWith('avz-')) {
    return themes.avz;
  }

  if (comparatorName.startsWith('cb-')) {
    return themes.cb;
  }

  return themes.default;
};

const getPropsFromErrorState: (
  props: InputTypeProps,
  _error: BaseWithFormProps['_error'],
  _errorProps: BaseWithFormProps['_errorProps'],
  _successProps: BaseWithFormProps['_successProps'],
) => WithFormProps<InputTypeProps> = (
  props,
  _error,
  _errorProps,
  _successProps,
) => {
  if (_error === undefined) {
    if (props.value === undefined || props.value === null || props.value === '')
      return props;
    return {
      ...props,
      ..._successProps,
      _labelProps: { ..._successProps._labelProps, ...props._labelProps },
    };
  }
  return {
    ...props,
    ..._errorProps,
    _labelProps: { ..._errorProps._labelProps, ...props._labelProps },
  };
};

const containsHTML = (str) => {
  const htmlRegex = /<[^>]*>/;
  return htmlRegex.test(str);
};

const errorAsJsx = (error: BaseWithFormProps['_error']) => {
  if (!error) return null;
  // TODO: have to check the following: either the type of BaseWithFormProps["_error"] is wrong or this check is useless
  // nb: it is most likely the type that is wrong and should be of type React.ReactNode
  const isErrorAnObject = typeof error !== 'string' && !Array.isArray(error);
  if (isErrorAnObject) return error;
  const errorAsString =
    typeof error === 'string' ? error : error.join('. ').replace('..', '.');

  const hasHTML = containsHTML(errorAsString);
  if (hasHTML) {
    return (
      <P
        mt={2}
        variant="footnoteError"
        dangerouslySetInnerHTML={{ __html: errorAsString }}
      />
    );
  } else {
    return (
      <P mt={2} variant="footnoteError">
        {errorAsString}
      </P>
    );
  }
};

export function withFormProps<P extends InputTypeProps>(
  Component: ComponentType<P>,
  options?: Options,
): React.FC<WithFormProps<P>> {
  return ({
    _label,
    _footnote,
    _description,
    _tooltip,
    _containerProps,
    _innerContainerProps,
    _error,
    _errorProps = {},
    _successProps = {},
    id,
    ...props
  }) => {
    const { name } = useContext(ComparatorContext);
    const theme = useMemo(() => getIconColor(name), [name]);

    const { _labelProps, ..._props } = useMemo(
      () => getPropsFromErrorState(props, _error, _errorProps, _successProps),
      [props, _error, _errorProps, _successProps],
    );
    const { trackTooltipClick } = useTooltipTracking();
    const error = useMemo(() => errorAsJsx(_error), [_error]);
    const footnote = useMemo(() => {
      const defaultFootnoteJsx = (
        <P mt={2} variant="footnoteInput">
          {_footnote}
        </P>
      );
      if (!_footnote) return null;
      if (typeof _footnote === 'string') return defaultFootnoteJsx;
      return _footnote;
    }, [_footnote]);

    const description = useMemo(() => {
      const defaultFootnoteJsx = (
        <P mt={2} variant="footnoteRegular">
          {_description}
        </P>
      );
      if (!_description) return null;
      if (typeof _description === 'string') return defaultFootnoteJsx;
      return _description;
    }, [_description]);

    const tooltip = useMemo(() => {
      switch (typeof _tooltip) {
        case 'string':
          return (
            <P
              variant="footnoteRegular"
              dangerouslySetInnerHTML={{ __html: _tooltip }}
            />
          );
        case 'object':
          return getRendererFromDescriptor(_tooltip as ComponentDescriptor);
        default:
          return _tooltip;
      }
    }, [_tooltip]);

    const _id = useMemo(() => {
      return id !== undefined
        ? id
        : `${props.name
            ?.toString()
            .toLowerCase()
            .replace(/\s/g, '-')}-${Math.floor(Math.random() * 100)}`;
    }, [props.name]);

    if (_props.hidden === true) return null;

    const labelComponent = (
      <Label htmlFor={_id} {..._labelProps}>
        {!_labelProps?.hasOwnProperty('dangerouslySetInnerHTML')
          ? _label
          : null}
      </Label>
    );

    return (
      <Div
        {...{
          variant: 'formGroup',
          ...options?._containerProps,
          ..._containerProps,
        }}
      >
        <Div display="flex" alignItems="flex-start" {..._innerContainerProps}>
          {_label ? labelComponent : null}
          {_tooltip && (
            <TooltipIcon
              ml={1}
              position="relative"
              bottom="1px"
              name="info"
              size={20}
              css={{ fill: theme.iconColor }}
              _tooltip={tooltip}
              cursor="pointer"
              fieldName={props?.name || id}
              trackTooltipClick={trackTooltipClick}
            />
          )}
        </Div>
        {_description && description}
        <Component
          {...(_props as React.ComponentProps<typeof Component>)}
          id={_id}
        />
        {footnote}
        {error}
      </Div>
    );
  };
}
